import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ArrowLeft, CalendarPlus, Check, Clock, MessageSquareText, Phone, Sparkles, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError, publicApi } from "@/api/client";
import type { CreateBookingResponse, PublicStylist, Service, Slot } from "@/api/contract";
import { Avatar } from "@/components/blocks";
import { CodeInput } from "@/components/CodeInput";
import { TimePicker } from "@/components/TimePicker";
import { Alert, Button, Card, Container, Eyebrow, Field, inputClass, PoleBar, Spinner } from "@/components/ui";
import { site } from "@/content";
import { useSalon } from "@/hooks/useSalon";
import { downloadIcs, icsFor } from "@/lib/ics";
import { duration, formatPhoneInput, isValidUsPhone, money, whenLabel } from "@/lib/format";

type Step = "service" | "stylist" | "time" | "details" | "verify" | "done";
const STEPS: { key: Step; label: string }[] = [
  { key: "service", label: "Service" },
  { key: "stylist", label: "Stylist" },
  { key: "time", label: "Time" },
  { key: "details", label: "Details" },
];

const ANY = "any";
const TOKEN_KEY = "sc.phoneToken";

interface SavedToken {
  phone: string;
  token: string;
  expiresAt: string;
}

function savedToken(phone: string): string | undefined {
  try {
    const t = JSON.parse(sessionStorage.getItem(TOKEN_KEY) ?? "null") as SavedToken | null;
    if (t && t.phone === phone && Date.parse(t.expiresAt) - Date.now() > 60_000) return t.token;
  } catch {
    /* ignore */
  }
  return undefined;
}

function Progress({ step }: { step: Step }) {
  const index = STEPS.findIndex((s) => s.key === step);
  const current = index === -1 ? STEPS.length : index;
  return (
    <ol className="flex items-center gap-2" aria-label="Booking progress">
      {STEPS.map((s, i) => (
        <li key={s.key} className="flex flex-1 flex-col gap-1.5">
          <span
            className={clsx("h-1.5 rounded-full transition-all", i < current ? "bg-rust" : i === current ? "pole-stripes animate-pole" : "bg-line")}
            aria-hidden
          />
          <span className={clsx("text-xs font-semibold", i <= current ? "text-ink" : "text-steel")} aria-current={i === current ? "step" : undefined}>
            {s.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Summary({ service, stylist, slot, anyStylist }: { service?: Service; stylist?: PublicStylist; slot?: Slot; anyStylist: boolean }) {
  if (!service) return null;
  return (
    <Card className="p-6">
      <Eyebrow className="text-steel">Your appointment</Eyebrow>
      <p className="mt-3 font-display text-2xl">{service.name}</p>
      <p className="mt-1 flex items-center gap-3 text-sm text-muted">
        <span className="inline-flex items-center gap-1">
          <Clock className="size-4" aria-hidden /> {duration(service.durationMinutes)}
        </span>
        <span className="font-semibold text-rust">{money(service.priceCents, service.priceFrom)}</span>
      </p>
      {(stylist || anyStylist) && (
        <p className="mt-4 flex items-center gap-2 text-sm">
          <User className="size-4 text-steel" aria-hidden /> {stylist ? stylist.name : "First available stylist"}
        </p>
      )}
      {slot && (
        <p className="mt-2 flex items-center gap-2 text-sm font-semibold">
          <CalendarPlus className="size-4 text-steel" aria-hidden /> {whenLabel(slot.start)}
        </p>
      )}
      <p className="mt-6 text-xs text-muted">Pay at the salon. Free changes up to 2 hours before.</p>
    </Card>
  );
}

function Choice({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        "flex w-full items-center gap-4 rounded-2xl bg-white p-4 text-left ring-1 transition hover:-translate-y-0.5 hover:shadow-soft",
        selected ? "ring-2 ring-rust" : "ring-line",
      )}
    >
      {children}
      <span className={clsx("ml-auto grid size-6 shrink-0 place-items-center rounded-full", selected ? "bg-rust text-white" : "ring-1 ring-line")} aria-hidden>
        {selected && <Check className="size-4" />}
      </span>
    </button>
  );
}

export function Book() {
  const { data, isLoading, isError } = useSalon();
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>("service");
  const [serviceId, setServiceId] = useState<string>();
  const [stylistId, setStylistId] = useState<string>();
  const [slot, setSlot] = useState<Slot>();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [code, setCode] = useState("");
  const [demoCode, setDemoCode] = useState<string>();
  const [error, setError] = useState<string>();
  const [touched, setTouched] = useState(false);
  const [result, setResult] = useState<CreateBookingResponse>();
  const [resendIn, setResendIn] = useState(0);

  const service = data?.services.find((s) => s.id === serviceId);
  const stylists = useMemo(() => (data && service ? data.stylists.filter((s) => s.bookable && service.stylistIds.includes(s.id)) : []), [data, service]);
  const chosenStylist = stylistId && stylistId !== ANY ? data?.stylists.find((s) => s.id === stylistId) : undefined;
  const phoneKey = phone.replace(/\D/g, "").slice(-10);

  // Deep links from service and stylist pages (/book?service=perm&stylist=betty).
  useEffect(() => {
    if (!data) return;
    const s = params.get("service");
    const st = params.get("stylist");
    if (s && data.services.some((x) => x.id === s)) {
      setServiceId(s);
      const offered = data.services.find((x) => x.id === s)!.stylistIds;
      if (st && offered.includes(st)) {
        setStylistId(st);
        setStep("time");
      } else setStep("stylist");
    } else if (st) setStylistId(st);
  }, [data]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setError(undefined);
  }, [step]);

  const book = useMutation({
    mutationFn: (phoneToken: string) =>
      publicApi.book({
        phoneToken,
        serviceId: serviceId!,
        stylistId: stylistId!,
        start: slot!.start,
        name: name.trim(),
        email: email.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (r) => {
      setResult(r);
      setStep("done");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "slot_taken") {
        setSlot(undefined);
        setStep("time");
        setTimeout(() => setError("Sorry, someone just booked that time. Please pick another."), 0);
        return;
      }
      if (err instanceof ApiError && err.code === "phone_unverified") {
        sessionStorage.removeItem(TOKEN_KEY);
        setStep("details");
      }
      setError(err.message);
    },
  });

  const sendCode = useMutation({
    mutationFn: () => publicApi.startVerification(phone),
    onSuccess: (r) => {
      setDemoCode(r.demoCode);
      setCode("");
      setResendIn(30);
      setStep("verify");
    },
    onError: (err) => setError(err.message),
  });

  const verify = useMutation({
    mutationFn: () => publicApi.confirmVerification(phone, code),
    onSuccess: (r) => {
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify({ phone: phoneKey, token: r.phoneToken, expiresAt: r.expiresAt } satisfies SavedToken));
      book.mutate(r.phoneToken);
    },
    onError: (err) => {
      setError(err.message);
      setCode("");
    },
  });

  // Submit as soon as the sixth digit lands (typed, pasted or autofilled).
  useEffect(() => {
    if (code.length === 6 && step === "verify" && !verify.isPending) verify.mutate();
  }, [code]);

  const nameError = touched && name.trim().length < 2 ? "Please enter your name." : undefined;
  const phoneError = touched && !isValidUsPhone(phone) ? "Enter a 10-digit US mobile number." : undefined;
  const emailError = touched && email && !/^\S+@\S+\.\S+$/.test(email) ? "That email doesn't look right." : undefined;

  function submitDetails(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (name.trim().length < 2 || !isValidUsPhone(phone) || (email && !/^\S+@\S+\.\S+$/.test(email))) return;
    const existing = savedToken(phoneKey);
    if (existing) book.mutate(existing);
    else sendCode.mutate();
  }

  const back: Partial<Record<Step, Step>> = { stylist: "service", time: "stylist", details: "time", verify: "details" };

  if (isLoading) return <Spinner label="Loading" />;
  if (isError || !data)
    return (
      <Container className="py-16">
        <Alert tone="error">
          Online booking isn't available right now. Please call <a href={`tel:${site.phone}`} className="font-semibold underline">{site.phoneDisplay}</a>.
        </Alert>
      </Container>
    );

  if (step === "done" && result) {
    const a = result.appointment;
    const manage = `/m/${result.manageToken}`;
    return (
      <Container className="max-w-2xl py-12 sm:py-20">
        <Card className="overflow-hidden text-center">
          <PoleBar animated />
          <div className="px-6 py-12 sm:px-12">
            <div className="mx-auto grid size-16 animate-fade-up place-items-center rounded-full bg-rust text-white shadow-lift">
              <Check className="size-8" aria-hidden />
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl">You're booked, {a.customerName.split(" ")[0]}!</h1>
            <p className="mt-3 text-muted">We've texted your confirmation. See you soon.</p>
            <div className="mt-8 rounded-2xl bg-sand p-6 text-left">
              <p className="font-display text-2xl">{a.serviceName}</p>
              <p className="mt-1 text-muted">with {a.stylistName}</p>
              <p className="mt-4 text-lg font-semibold">{whenLabel(a.start)}</p>
              <p className="mt-1 text-sm text-muted">
                {site.address.line1}, {site.address.city} · {duration(a.durationMinutes)} · {money(a.priceCents, a.priceFrom)}
              </p>
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <Button
                size="lg"
                onClick={() =>
                  downloadIcs(
                    "style-and-curl.ics",
                    icsFor({
                      id: a.id,
                      title: `${a.serviceName} at Style & Curl`,
                      start: a.start,
                      end: a.end,
                      location: `Style & Curl, ${site.address.line1}, ${site.address.city}, ${site.address.state} ${site.address.zip}`,
                      description: `With ${a.stylistName}. Manage: ${window.location.origin}${manage}`,
                    }),
                  )
                }
              >
                <CalendarPlus className="size-5" aria-hidden /> Add to calendar
              </Button>
              <Link to={manage} className="inline-flex h-14 items-center justify-center rounded-full font-semibold ring-1 ring-line hover:text-rust">
                Change or cancel
              </Link>
            </div>
            <p className="mt-6 text-sm text-muted">
              Questions? Call <a href={`tel:${site.phone}`} className="font-semibold text-rust">{site.phoneDisplay}</a>
            </p>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-10 sm:py-14">
      <div className="mb-8 flex items-center gap-4">
        {back[step] ? (
          <button type="button" onClick={() => setStep(back[step]!)} className="grid size-11 place-items-center rounded-full ring-1 ring-line hover:text-rust" aria-label="Back">
            <ArrowLeft className="size-5" />
          </button>
        ) : (
          <Link to="/" className="grid size-11 place-items-center rounded-full ring-1 ring-line hover:text-rust" aria-label="Back to home">
            <ArrowLeft className="size-5" />
          </Link>
        )}
        <div className="flex-1">
          <Progress step={step === "verify" ? "details" : step} />
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div key={step} className="animate-fade-up">
          {error && (
            <div className="mb-6">
              <Alert tone="error">{error}</Alert>
            </div>
          )}

          {step === "service" && (
            <section>
              <h1 className="font-display text-4xl">What are we doing today?</h1>
              {[...new Set(data.services.map((s) => s.category))].map((cat) => (
                <div key={cat} className="mt-8">
                  <h2 className="eyebrow text-steel">{cat}</h2>
                  <div className="mt-3 grid gap-3">
                    {data.services
                      .filter((s) => s.category === cat)
                      .map((s) => (
                        <Choice
                          key={s.id}
                          selected={s.id === serviceId}
                          onClick={() => {
                            setServiceId(s.id);
                            setSlot(undefined);
                            if (stylistId && stylistId !== ANY && !s.stylistIds.includes(stylistId)) setStylistId(undefined);
                            setStep("stylist");
                          }}
                        >
                          <div className="min-w-0">
                            <p className="font-semibold">{s.name}</p>
                            <p className="mt-0.5 text-sm text-muted">{s.description}</p>
                            <p className="mt-1.5 text-xs font-medium text-steel">{duration(s.durationMinutes)}</p>
                          </div>
                          <span className="ml-auto pl-2 font-display text-xl text-rust">{money(s.priceCents, s.priceFrom)}</span>
                        </Choice>
                      ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {step === "stylist" && service && (
            <section>
              <h1 className="font-display text-4xl">Who would you like?</h1>
              <div className="mt-8 grid gap-3">
                {stylists.length > 1 && (
                  <Choice
                    selected={stylistId === ANY}
                    onClick={() => {
                      setStylistId(ANY);
                      setSlot(undefined);
                      setStep("time");
                    }}
                  >
                    <span className="grid size-12 place-items-center rounded-full bg-rust-50 text-rust" aria-hidden>
                      <Sparkles className="size-6" />
                    </span>
                    <div>
                      <p className="font-semibold">First available</p>
                      <p className="text-sm text-muted">Most open times</p>
                    </div>
                  </Choice>
                )}
                {stylists.map((s) => (
                  <Choice
                    key={s.id}
                    selected={stylistId === s.id}
                    onClick={() => {
                      setStylistId(s.id);
                      setSlot(undefined);
                      setStep("time");
                    }}
                  >
                    <Avatar name={s.name} color={s.color} photoUrl={s.photoUrl} />
                    <div>
                      <p className="font-semibold">{s.name}</p>
                      <p className="text-sm text-muted">{s.title}</p>
                    </div>
                  </Choice>
                ))}
              </div>
            </section>
          )}

          {step === "time" && service && stylistId && (
            <section>
              <h1 className="font-display text-4xl">Pick a time</h1>
              <div className="mt-8">
                <TimePicker
                  fetcher={publicApi.availability}
                  scope="public"
                  serviceId={service.id}
                  stylistId={stylistId}
                  selected={slot}
                  onSelect={(s) => {
                    setSlot(s);
                    setStep("details");
                  }}
                />
              </div>
            </section>
          )}

          {step === "details" && (
            <section>
              <h1 className="font-display text-4xl">Almost done</h1>
              <p className="mt-2 text-muted">We'll text a code to confirm your number, then send your confirmation and a reminder.</p>
              <div className="mt-6 lg:hidden">
                <Summary service={service} stylist={chosenStylist} slot={slot} anyStylist={stylistId === ANY} />
              </div>
              <form className="mt-8 grid gap-5" onSubmit={submitDetails} noValidate>
                <Field label="Your name" error={nameError}>
                  <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={80} />
                </Field>
                <Field label="Mobile number" hint="US numbers only. We only text about your appointment." error={phoneError}>
                  <input
                    className={inputClass}
                    value={phone}
                    onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                    autoComplete="tel-national"
                    inputMode="tel"
                    placeholder="(972) 555-0123"
                    required
                  />
                </Field>
                <Field label="Email (optional)" error={emailError}>
                  <input className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" maxLength={120} />
                </Field>
                <Field label="Anything we should know? (optional)">
                  <textarea
                    className={clsx(inputClass, "min-h-24 resize-y")}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={500}
                    placeholder="e.g. first visit, bringing photos, sensitive scalp"
                  />
                </Field>
                <Button type="submit" size="lg" loading={sendCode.isPending || book.isPending}>
                  <MessageSquareText className="size-5" aria-hidden /> Text me a code
                </Button>
                <p className="text-center text-xs text-muted">Message and data rates may apply. Reply STOP to opt out.</p>
              </form>
            </section>
          )}

          {step === "verify" && (
            <section className="text-center">
              <h1 className="font-display text-4xl">Enter your code</h1>
              <p className="mt-2 text-muted">
                We texted a 6-digit code to <span className="font-semibold text-ink">{phone}</span>.
              </p>
              {demoCode && (
                <div className="mx-auto mt-6 max-w-sm">
                  <Alert tone="info">
                    <span className="font-semibold">Preview mode:</span> texting turns on after carrier approval. Your code is{" "}
                    <button type="button" className="font-display text-lg text-rust underline" onClick={() => setCode(demoCode)}>
                      {demoCode}
                    </button>
                  </Alert>
                </div>
              )}
              <div className="mt-8">
                <CodeInput value={code} onChange={setCode} disabled={verify.isPending || book.isPending} />
              </div>
              {(verify.isPending || book.isPending) && <Spinner label="Booking your appointment" />}
              <div className="mt-8 text-sm text-muted">
                Didn't get it?{" "}
                <button
                  type="button"
                  className="font-semibold text-rust disabled:text-steel"
                  disabled={resendIn > 0 || sendCode.isPending}
                  onClick={() => sendCode.mutate()}
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>{" "}
                or{" "}
                <button type="button" className="font-semibold text-rust" onClick={() => setStep("details")}>
                  change number
                </button>
              </div>
            </section>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-4">
            <Summary service={service} stylist={chosenStylist} slot={slot} anyStylist={stylistId === ANY} />
            <Card className="flex items-center gap-3 p-5 text-sm">
              <Phone className="size-5 text-rust" aria-hidden />
              <span>
                Rather call? <a href={`tel:${site.phone}`} className="font-semibold text-rust">{site.phoneDisplay}</a>
              </span>
            </Card>
          </div>
        </aside>
      </div>
    </Container>
  );
}
